import { useState } from 'react';
import { Input } from '@aaimplatform/ui';
import type { InputProps } from '@aaimplatform/ui';
import { parsePct, pctText } from './percent';

interface PercentInputProps extends Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'onFocus' | 'type'> {
  /** The stored ratio (0.85), or undefined when an optional field is unset. */
  value: number | undefined;
  /** Called with the parsed ratio as the user types. */
  onChange: (ratio: number | undefined) => void;
  /**
   * An optional field may be cleared, which reports undefined. A required
   * field keeps its stored value while its text is empty or unparseable, so an
   * emptied box never saves as 0.
   */
  optional?: boolean;
}

/**
 * A number input over a stored ratio, shown as a percent.
 *
 * The editors used to bind the input's value to the formatted ratio, so every
 * keystroke was re-formatted: selecting 80.0 and typing 85 left 8.1, and a
 * whole-number field turned 12.5 into 13 (r3 fi-g7-2). This input keeps the
 * raw text the user is typing while it has focus, reports the parsed ratio on
 * each change, and formats the stored value again on blur.
 */
export function PercentInput({ value, onChange, optional = false, ...inputProps }: PercentInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const stored = value === undefined || value === null ? '' : pctText(value);

  return (
    <Input
      {...inputProps}
      type="number"
      value={draft ?? stored}
      onFocus={() => setDraft(stored)}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const ratio = parsePct(raw);
        if (ratio !== undefined) onChange(ratio);
        else if (optional && raw.trim() === '') onChange(undefined);
      }}
      onBlur={() => setDraft(null)}
    />
  );
}
